import type { TFunction } from 'i18next'
import type { TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import type { ValueCheckBaseValuationWidgetCopy } from 'ComponentLibrary/src/widgets/ValueCheckWidgets/ValueCheckBaseValuationWidget'
import type { ValueCheckOverviewMetricGridCopy } from 'ComponentLibrary/src/widgets/ValueCheckWidgets/ValueCheckOverviewMetricGrid'
import type { ValueCheckSensitivityMatrixWidgetCopy } from 'ComponentLibrary/src/widgets/ValueCheckWidgets/ValueCheckSensitivityMatrixWidget'
import type { ValueCheckTenantSummaryWidgetCopy } from 'ComponentLibrary/src/widgets/ValueCheckWidgets/ValueCheckTenantSummaryWidget'

const tenantUsageTypeKeys = [
    'residential',
    'commercial',
    'gastronomy',
    'industry',
    'hotel',
    'storage',
    'underground_parking',
    'leisure',
    'social',
    'archive',
    'outdoor_parking',
    'parking',
    'retail',
    'logistics',
    'office',
    'other',
    'total',
    'other_parking',
    'open_space',
    'other_use',
    'garage_parking',
] as const satisfies TenantUsageTypeLabelKeys[]

const getTenantUsageTypeLabels = (tCommon: TFunction): Record<TenantUsageTypeLabelKeys, string> =>
    Object.fromEntries(tenantUsageTypeKeys.map((key) => [key, tCommon(`tenantUsageTypes.${key}`)])) as Record<
        TenantUsageTypeLabelKeys,
        string
    >

export const getValueCheckOverviewMetricGridCopy = (
    tCommon: TFunction,
    tValueCheckOverviewMetricGrid: TFunction
): ValueCheckOverviewMetricGridCopy => {
    const mortgageLendingValueComparisonLabel = tValueCheckOverviewMetricGrid('mortgageLendingValueComparisonLabel')

    return {
        subheadlines: {
            changeFromPreviousValuation: tValueCheckOverviewMetricGrid('changeFromPreviousValuation'),
            marketValue: tCommon('marketValue'),
        },
        statusLabels: {
            marketValueConfirmed: tCommon('marketValueConfirmed'),
            marketValueNotConfirmed: tCommon('marketValueNotConfirmed'),
        },
        comparisonLabels: {
            greaterThanMarketValue: `${mortgageLendingValueComparisonLabel} > ${tCommon('marketValue')}`,
            equalToMarketValue: `${mortgageLendingValueComparisonLabel} = ${tCommon('marketValue')}`,
            lessThanMarketValue: `${mortgageLendingValueComparisonLabel} < ${tCommon('marketValue')}`,
        },
    }
}

export const getValueCheckBaseValuationWidgetCopy = (
    tCommon: TFunction,
    tActions: TFunction,
    tValueCheckBaseValuationWidget: TFunction
): ValueCheckBaseValuationWidgetCopy => ({
    title: tValueCheckBaseValuationWidget('title'),
    valuationLabel: tCommon('valuationReport'),
    marketValueLabel: tCommon('marketValue'),
    mortgageLendingValueLabel: tCommon('mortgageLendingValue'),
    actionLabel: tActions('viewValuation'),
})

export const getValueCheckTenantSummaryWidgetCopy = (
    tCommon: TFunction,
    tTenantBreakdownTable: TFunction,
    tValueCheckTenantSummaryWidget: TFunction
): ValueCheckTenantSummaryWidgetCopy => ({
    title: tCommon('tenantList'),
    categoryLabel: tValueCheckTenantSummaryWidget('categoryLabel'),
    baseLabel: tValueCheckTenantSummaryWidget('baseLabel'),
    valueCheckLabel: tCommon('valueCheck'),
    changeLabel: tCommon('change'),
    groupLabels: {
        rentable: tValueCheckTenantSummaryWidget('groupLabels.rentable'),
        rented: tValueCheckTenantSummaryWidget('groupLabels.rented'),
        vacant: tValueCheckTenantSummaryWidget('groupLabels.vacant'),
    },
    rowLabels: {
        area: tValueCheckTenantSummaryWidget('rowLabels.area'),
        units: tValueCheckTenantSummaryWidget('rowLabels.units'),
        parkingSpaces: tValueCheckTenantSummaryWidget('rowLabels.parkingSpaces'),
    },
    usageTypeLabels: getTenantUsageTypeLabels(tCommon),
    getUnitLabel: (count, isParking) =>
        isParking ? tTenantBreakdownTable('units.parkingSpace', { count }) : tTenantBreakdownTable('units.unit', { count }),
})

export const getValueCheckSensitivityMatrixWidgetCopy = (tCommon: TFunction): ValueCheckSensitivityMatrixWidgetCopy => ({
    title: tCommon('sensitivityMatrix'),
    netYieldLabel: tCommon('netYield'),
    marketRentLabel: tCommon('marketRent'),
    percentagePointUnit: tCommon('percentagePoints'),
})
